import { FC, ReactNode, useRef } from "react";
import { Form, Field, FormSpy } from "react-final-form";
import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { useToast } from "ui-kit";
import client from "libs/client";
import {
	AdminAsyncSelectAdapter,
	AdminCreatableSelectAdapter,
	AdminSelectAdapter,
} from "components/select";
import { GenderOptions } from "features/discover/types";
import { Continents, GameOptions } from "pages/productManagement/types";
import { getApiErrorMessage } from "libs/utils";
import type { continentCountries } from "pages/productManagement/types";
import type { SubscriptionProductConfigResponse, SubscriptionRowResponse, SubscriptionUpsertRequest } from "libs/apiClient";
import { resolveProductBehavior, type SubscriptionBehavior } from "./subscriptionProductConfig";
import {
	buildSubscriptionInitialValues,
	buildSubscriptionPayload,
	type SelectOption,
	type SubscriptionFormValues,
} from "./subscriptionFormUtils";
import { DiscoverCountriesField, type DiscoverCountriesFieldHandle } from "./DiscoverCountriesField";

interface SubscriptionFormProps {
	row?: SubscriptionRowResponse;
	productsConfig?: SubscriptionProductConfigResponse;
	countriesWithContinents: continentCountries[];
	subscriptionProducts: SelectOption[];
	ownershipOptions: SelectOption[];
	teamGroupOptions: SelectOption[];
	competitionOptions: SelectOption[];
	competitionEditionOptions: SelectOption[];
	eyeballAddons: SelectOption[];
	discoverCountryOptions: SelectOption[];
	canEditPipedriveDealId?: boolean;
	submitError?: string | null;
	isSubmitting?: boolean;
	onSubmit: (payload: SubscriptionUpsertRequest) => Promise<void>;
}

function FormSection({ title, children }: { title: string; children: ReactNode }) {
	return (
		<section className="subscriptionEditForm-section">
			<h2 className="subscriptionEditForm-sectionTitle">{title}</h2>
			{children}
		</section>
	);
}

function FormField({
	label,
	required = false,
	fullWidth = false,
	children,
}: {
	label: string;
	required?: boolean;
	fullWidth?: boolean;
	children?: ReactNode;
}) {
	return (
		<div className={`subscriptionEditForm-field ${fullWidth ? "subscriptionEditForm-field--full" : ""}`}>
			<div className={`cellTitle ${required ? "cellTitle--required" : ""}`}>{label}</div>
			<div className="subscriptionEditForm-input">{children}</div>
		</div>
	);
}

function hasProductOptionFields(behavior?: SubscriptionBehavior): boolean {
	if (!behavior) {
		return false;
	}

	return !!(
		behavior.hasCountries ||
		behavior.hasNationalContinents ||
		behavior.hasDiscoverCountries ||
		behavior.hasTeamGroup ||
		behavior.hasCompetitionAccess ||
		behavior.hasOwnerships ||
		behavior.hasGameOptions ||
		behavior.hasAddons ||
		behavior.hasGenderOptions ||
		behavior.hasAccountsLimit
	);
}

export const SubscriptionForm: FC<SubscriptionFormProps> = ({
	row,
	productsConfig,
	countriesWithContinents,
	subscriptionProducts,
	ownershipOptions,
	teamGroupOptions,
	competitionOptions,
	competitionEditionOptions,
	eyeballAddons,
	discoverCountryOptions,
	canEditPipedriveDealId = false,
	submitError = null,
	isSubmitting = false,
	onSubmit,
}) => {
	const { addErrorToast } = useToast();
	const discoverCountriesFieldRef = useRef<DiscoverCountriesFieldHandle>(null);

	const gameOptionsNames: Record<string, string> = {
		[GameOptions.AI]: _("label.AI"),
		[GameOptions.STATISTICS_RATING]: _("label.statistics_rating"),
		[GameOptions.STATISTICS_PLAYERS]: _("label.statistics_player"),
		[GameOptions.STATISTICS_TEAMS]: _("label.statistics_team"),
		[GameOptions.EVENTS]: _("label.events"),
		[GameOptions.DOWNLOADABLE]: _("label.download"),
		[GameOptions.CLIPS]: _("label.clips"),
		[GameOptions.DOWNLOADABLE_GAMETAGS]: _("label.downloadable_gametags"),
		[GameOptions.PLAYER_STATS_SEARCH]: _("label.player_stats_search"),
		[GameOptions.PHYSICAL_METRICS]: _("label.physical_metrics"),
		[GameOptions.CLIPS_ACROSS_GAMES]: _("label.clips_across_games"),
	};

	const genderOptionLabels: Record<string, string> = GenderOptions.reduce<Record<string, string>>(
		(acc, { value, label }) => ({ ...acc, [value]: label }),
		{},
	);

	const nationalContinentOptions: SelectOption[] = [
		{ label: _("national_team.africa"), value: Continents.AFRICA },
		{ label: _("national_team.asia"), value: Continents.ASIA },
		{ label: _("national_team.europe"), value: Continents.EUROPE },
		{ label: _("national_team.oceania"), value: Continents.OCEANIA },
		{ label: _("national_team.southAmerica"), value: Continents.SOUTH_AMERICA },
		{ label: _("national_team.northAmerica"), value: Continents.NORTH_AMERICA },
	];

	const initialValues = buildSubscriptionInitialValues(row, {
		productsConfig,
		ownershipOptions,
		teamGroupOptions,
		competitionOptions,
		competitionEditionOptions,
		countriesWithContinents,
		nationalContinentOptions,
		discoverCountryOptions,
	});
	const clubSearch = async (term: string) => {
		if (term === "") return [];
		const data = await client.clubProSearch.query({ search: term }).then((res) => res?.data);
		return data?.length ? data : [];
	};

	const handleFormSubmit = async (values: SubscriptionFormValues) => {
		const productValue = values.product?.value ? String(values.product.value) : null;
		const behavior = resolveProductBehavior(productsConfig, productValue);
		if (!behavior) {
			addErrorToast(_("message.select_product"));
			return;
		}

		if (
			behavior.hasDiscoverCountries &&
			discoverCountriesFieldRef.current?.hasPendingSelection() === true
		) {
			addErrorToast("Please select at least one Discover country");
			return;
		}

		const result = buildSubscriptionPayload(
			values,
			behavior,
			row,
			eyeballAddons.map((addon) => String(addon.value)),
		);

		if (!result.ok) {
			addErrorToast(_(result.errorKey as Keys));
			return;
		}

		await onSubmit(result.payload);
	};

	return (
		<Form
			keepDirtyOnReinitialize
			onSubmit={handleFormSubmit}
			initialValues={initialValues}
			render={({ handleSubmit }) => (
					<form onSubmit={handleSubmit}>
						{submitError ? (
							<div className="subscriptionEditForm-alert" role="alert">
								<strong>{getApiErrorMessage(submitError)}</strong>
							</div>
						) : null}

						<FormSection title="General">
							<div className="subscriptionEditForm-grid subscriptionEditForm-grid--3">
								<FormField label={_("label.product")} required>
									<Field name="product" component={AdminCreatableSelectAdapter} options={subscriptionProducts} />
								</FormField>

								<FormField label={_("label.pro_club")} required>
									<Field
										cacheOptions
										name="proClub"
										loadOptions={clubSearch}
										component={AdminAsyncSelectAdapter}
										placeholder={_("label.search")}
									/>
								</FormField>

								<FormField label={_("label.pipedrive_deal_id")} required={canEditPipedriveDealId}>
									<Field name="pipedriveDealId">
										{({ input }) => (
											<input {...input} maxLength={255} className="form-control" required={canEditPipedriveDealId} />
										)}
									</Field>
								</FormField>
							</div>

							<div className="subscriptionEditForm-grid">
								<FormField label={_("label.form.start_date")} required>
									<Field name="startDate" type="date">
										{({ input, meta }) => (
											<>
												<input {...input} maxLength={96} className="form-control" required />
												{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
											</>
										)}
									</Field>
								</FormField>

								<FormField label={_("label.form.expiration_date")} required>
									<FormSpy subscription={{ values: true }}>
										{({ values }) => (
											<Field name="expirationDate" type="date">
												{({ input, meta }) => (
													<>
														<input
															{...input}
															min={values.startDate || undefined}
															className="form-control"
															required
														/>
														{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
													</>
												)}
											</Field>
										)}
									</FormSpy>
								</FormField>
							</div>
						</FormSection>

						<FormSpy subscription={{ values: true }}>
							{({ values }) => {
								const productValue = values?.product?.value ? String(values.product.value) : null;
								const behavior = resolveProductBehavior(productsConfig, productValue);
								const showProductOptions = hasProductOptionFields(behavior);

								return (
									<>
										{showProductOptions ? (
											<FormSection title="Product options">
												<div className="subscriptionEditForm-grid">
													{behavior?.hasCountries && (
														<FormField label={_("videos.table.th.country")} required fullWidth>
															<Field
																name="countries"
																component={AdminSelectAdapter}
																isMulti
																options={countriesWithContinents}
															/>
														</FormField>
													)}

													{behavior?.hasNationalContinents && (
														<FormField label={_("label.form.nationalCountries")} fullWidth>
															<Field
																name="nationalCountries"
																component={AdminSelectAdapter}
																isMulti
																options={nationalContinentOptions}
															/>
														</FormField>
													)}

													{behavior?.hasDiscoverCountries && (
														<DiscoverCountriesField
															ref={discoverCountriesFieldRef}
															discoverCountryOptions={discoverCountryOptions}
															subscriptionRowId={row?.id}
														/>
													)}

													{behavior?.hasAccountsLimit && (
														<FormField label={_("label.max_accounts")} required>
															<Field name="maxAccounts" type="number">
																{({ input, meta }) => (
																	<>
																		<input
																			{...input}
																			min={0}
																			className="form-control"
																			required
																		/>
																		{meta.error && meta.touched && (
																			<span className="form-error">{meta.error}</span>
																		)}
																	</>
																)}
															</Field>
														</FormField>
													)}

													{behavior?.hasTeamGroup && (
														<FormField label="Team group" required>
															<Field
																name="teamGroupId"
																component={AdminSelectAdapter}
																isClearable
																options={teamGroupOptions}
															/>
														</FormField>
													)}

													{behavior?.hasCompetitionAccess && (
														<>
															<FormField label="Competition template" fullWidth>
																<Field
																	name="competitionIds"
																	component={AdminSelectAdapter}
																	isMulti
																	isClearable
																	options={competitionOptions}
																/>
															</FormField>
															<FormField label="Competition edition" fullWidth>
																<Field
																	name="competitionEditionIds"
																	component={AdminSelectAdapter}
																	isMulti
																	isClearable
																	options={competitionEditionOptions}
																/>
															</FormField>
														</>
													)}

													{behavior?.hasOwnerships && (
														<FormField label={_("label.ownership")} fullWidth>
															<Field
																name="ownership"
																component={AdminSelectAdapter}
																isMulti
																options={ownershipOptions}
															/>
														</FormField>
													)}

													{behavior?.hasGameOptions && (
														<FormField
															label={_("label.game_options")}
															required={behavior.hasGameOptionsRequired}
															fullWidth
														>
															<div className="subscriptionEditForm-checklist">
																{Object.entries(gameOptionsNames).map(([key, label]) => (
																	<label key={key} htmlFor={`game-option-${key}`}>
																		<Field
																			name="gameOptions"
																			component="input"
																			type="checkbox"
																			value={key}
																			id={`game-option-${key}`}
																		/>
																		<span>{label}</span>
																	</label>
																))}
															</div>
														</FormField>
													)}

													{behavior?.hasAddons && (
														<FormField
															label={_("label.eyeball_addons")}
															required={behavior.hasAddonsRequired}
															fullWidth
														>
															<div className="subscriptionEditForm-checklist">
																{eyeballAddons.map((addon) => (
																	<label key={String(addon.value)} htmlFor={`addon-${addon.value}`}>
																		<Field
																			name="eyeballAddons"
																			component="input"
																			type="checkbox"
																			value={addon.value}
																			id={`addon-${addon.value}`}
																		/>
																		<span>{addon.label}</span>
																	</label>
																))}
															</div>
														</FormField>
													)}

													{behavior?.hasGenderOptions && (
														<FormField label={_("label.subscription.competition")} required fullWidth>
															<div className="subscriptionEditForm-checklist">
																{Object.entries(genderOptionLabels).map(([key, label]) => (
																	<label key={key} htmlFor={`gender-${key}`}>
																		<Field
																			name="genderOptions"
																			component="input"
																			type="checkbox"
																			value={key}
																			id={`gender-${key}`}
																		/>
																		<span>{label}</span>
																	</label>
																))}
															</div>
														</FormField>
													)}
												</div>
											</FormSection>
										) : null}

										<FormSection title={_("label.subscription.comment")}>
											<div className="subscriptionEditForm-grid">
												<div className="subscriptionEditForm-field subscriptionEditForm-field--full">
													<div className="subscriptionEditForm-input">
														<Field name="comment">
															{({ input }) => (
																<textarea
																	{...input}
																	readOnly={behavior?.isCommentReadonly ?? false}
																	maxLength={256}
																	className="form-control textarea"
																/>
															)}
														</Field>
													</div>
												</div>
											</div>
										</FormSection>
									</>
								);
							}}
						</FormSpy>

						<div className="subscriptionEditForm-actions">
							<button type="submit" className="btn btn-light-blue" disabled={isSubmitting}>
								{_("btn.save")}
							</button>
						</div>
					</form>
				)}
		/>
	);
};

export default SubscriptionForm;
